import { useState, useCallback, useEffect } from 'react';
import { produce } from 'immer';
import moment from 'moment-timezone';
import {
  IconDefinition,
  faBell,
  faBox,
  faBoxOpen,
  faCheck,
  faEye,
  faEraser,
  faMoon,
  faNotesMedical,
  faPauseCircle,
  faPencil,
  faPlayCircle,
  faStickyNote,
  faComments,
  faTrash,
  faUndo,
  faUserPlus,
  faShare,
  faAlarmClock,
  faStopwatch,
  faCommentSlash,
  faPhoneVolume,
  faCommentDots,
  faPhoneSlash
} from '@fortawesome/pro-solid-svg-icons';

import { useMetaData } from '../contexts/metadata';
import { Terms } from '../contexts/terms/types';

import { AppKey } from './apps';
import { IPatient } from './patient';
import type { IUser } from './user';
import {
  PostJSON,
  PaginatedParams,
  BaseResponse,
  ErrorResponse
} from './request';
import useRequestJSON, { useRequestOnEvent } from './hooks/useRequestJSON';
import { DateSerialized } from '@lightbeamhealth/blueprint-form';
import { Option } from 'blueprint-form/components/types';
import { Capitalize } from './format';

export enum EventTypes {
  // Generic Notes
  Note = 'n',

  // Patient Events
  PatientAdded = 'pa',
  PatientArchived = 'pc',
  PatientUnarchived = 'pcu',
  PatientDeleted = 'pd',
  PatientEdited = 'pe',
  PatientRemoved = 'pr',
  PatientResolved = 'pr',
  PatientSnoozed = 'ps',
  PatientUnsnoozed = 'psu',
  PatientSuspended = 'pp',
  PatientUnsuspended = 'ppu',
  PatientOptOut = 'po',
  PatientOptOutScheduleCanceled = 'poc',
  PatientLandlineDetected = 'pll',

  // Prescription events
  Prescription = 'rx',
  PrescriptionCanceled = 'rxc',
  PrescriptionEdited = 'rxe',

  // Alert events
  Alert = 'a',
  AlertActionNote = 'an',
  AlertResolved = 'ar',
  AlertSnoozed = 'as',
  AlertUnsnoozed = 'asu',
  AlertUnresolved = 'aru',

  // Miscellaneous events
  EMROrder = 'eo',
  RecentlyEnrolledSeen = 'rs',
  SessionInvalidated = 'si',
  PushMessage = 'pm',
  TimeTracked = 'tt',
  ChatStarted = 'cs',
  ChatRead = 'cr',
  ChatUnread = 'cu',

  // Inbound events
  PatientCallIn = 'pci',
  PatientTextIn = 'pti'
}
export interface EventTypeDescription {
  description: string;
  icon: IconDefinition;
  iconColor:
    | 'text-gray2' // General note icon color
    | 'text-blue3' // Alerts
    | 'text-brand' // Primary user actions (archive, cancel, pause, snooze)
    | 'text-green'; // Resolve
}
export const getEventTypes = (
  terms: Terms
): Record<EventTypes, EventTypeDescription> => {
  return {
    n: {
      description: 'Note added ',
      icon: faStickyNote,
      iconColor: 'text-brand'
    },
    pa: {
      description: `Added ${terms.patient.capitalized}`,
      icon: faUserPlus,
      iconColor: 'text-gray2'
    },
    pe: {
      description: `Edited ${terms.patient.capitalized}`,
      icon: faPencil,
      iconColor: 'text-gray2'
    },
    pd: {
      description: `Removed ${terms.patient.capitalized}`,
      icon: faTrash,
      iconColor: 'text-brand'
    },
    pc: {
      description: `Archived ${terms.patient.capitalized}`,
      icon: faBox,
      iconColor: 'text-brand'
    },
    pcu: {
      description: `Unarchived ${terms.patient.capitalized}`,
      icon: faBoxOpen,
      iconColor: 'text-brand'
    },
    pp: {
      description: `${terms.patient.capitalized}'s Service Paused`,
      icon: faPauseCircle,
      iconColor: 'text-brand'
    },
    ppu: {
      description: `${terms.patient.capitalized}'s Service Resumed`,
      icon: faPlayCircle,
      iconColor: 'text-brand'
    },
    po: {
      description: `${terms.patient.capitalized} Opted Out`,
      icon: faCommentSlash,
      iconColor: 'text-brand'
    },
    poc: {
      description: `${terms.patient.capitalized} Opted Out and Prescription Canceled`,
      icon: faTrash,
      iconColor: 'text-brand'
    },
    pr: {
      description: `Resolved ${terms.patient.capitalized}`,
      icon: faCheck,
      iconColor: 'text-green'
    },
    ps: {
      description: `Snoozed ${terms.patient.capitalized}`,
      icon: faMoon,
      iconColor: 'text-brand'
    },
    psu: {
      description: `Unsnoozed ${terms.patient.capitalized}`,
      icon: faAlarmClock,
      iconColor: 'text-brand'
    },
    rx: {
      description: 'Prescription Added',
      icon: faNotesMedical,
      iconColor: 'text-gray2'
    },
    rxe: {
      description: 'Prescription Updated',
      icon: faPencil,
      iconColor: 'text-gray2'
    },
    rxc: {
      description: 'Prescription Canceled',
      icon: faTrash,
      iconColor: 'text-brand'
    },
    a: {
      description: 'Alert',
      icon: faBell,
      iconColor: 'text-blue3'
    },
    as: {
      description: 'Alert Snoozed',
      icon: faMoon,
      iconColor: 'text-brand'
    },
    asu: {
      description: 'Alert Unsnoozed',
      icon: faAlarmClock,
      iconColor: 'text-brand'
    },
    ar: {
      description: 'Alert Resolved',
      icon: faCheck,
      iconColor: 'text-green'
    },
    aru: {
      description: 'Alert Unresolved',
      icon: faUndo,
      iconColor: 'text-gray2'
    },
    an: {
      description: 'Alert Action Notes',
      icon: faStickyNote,
      iconColor: 'text-gray2'
    },
    si: {
      description: 'Value Invalidated',
      icon: faEraser,
      iconColor: 'text-brand'
    },
    eo: {
      description: 'EMR Order',
      icon: faNotesMedical,
      iconColor: 'text-gray2'
    },
    rs: {
      description: 'Recently Enrolled Seen',
      icon: faEye,
      iconColor: 'text-gray2'
    },
    pm: {
      description: 'Push Message',
      icon: faShare,
      iconColor: 'text-green'
    },
    tt: {
      description: 'Time Tracked',
      icon: faStopwatch,
      iconColor: 'text-brand'
    },
    cs: {
      description: 'Chat Started',
      icon: faComments,
      iconColor: 'text-brand'
    },
    cr: {
      description: 'Chat Read',
      icon: faComments,
      iconColor: 'text-gray2'
    },
    cu: {
      description: 'Chat Unread',
      icon: faComments,
      iconColor: 'text-gray2'
    },
    pci: {
      description: `${terms.patient.capitalized} Called In`,
      icon: faPhoneVolume,
      iconColor: 'text-blue3'
    },
    pti: {
      description: `${terms.patient.capitalized} Texted In`,
      icon: faCommentDots,
      iconColor: 'text-blue3'
    },
    pll: {
      description: 'SMS Delivery Issue (Landline/Unreachable)',
      icon: faPhoneSlash,
      iconColor: 'text-brand'
    }
  };
};

export interface IEvent {
  accountId: number;
  app: AppKey;
  contactId: number;
  createdAt: Date;
  deletedAt: Date;
  id: number;
  meta: string;
  patient: IPatient;
  patientId: number;
  type: EventTypes;
  user?: Pick<IUser, 'id' | 'email'>;
  userId?: number;
  _messages?: Array<{
    body: string;
  }>;
}

// Snooze-related Types & Utils

export interface SnoozeFormData {
  id?: number;
  ids?: number[];
  duration?: string;
  snoozeUntil?: string; // NOTE: DB uses alert.snoozedUntil but endpoint requires 'snoozeUntil', no 'd'
  reason: string;
  note: string;
  tz: string;
  misc: string;
}

export type SnoozeFormDataRaw = Omit<
  DateSerialized<SnoozeFormData, 'snoozeUntil'>,
  'duration'
> & {
  duration?: string;
};

export const serializeSnoozeFormData = (
  data: SnoozeFormDataRaw
): SnoozeFormData => {
  return {
    ...data,
    snoozeUntil: data.snoozeUntil
      ? moment.tz(data.snoozeUntil, data.tz).format()
      : null,
    duration: data.duration != null ? data.duration : null
  };
};

export const deserializeSnoozeFormData = (
  data: SnoozeFormData
): SnoozeFormDataRaw => {
  return {
    ...data,
    snoozeUntil: data.snoozeUntil
      ? moment.tz(data.snoozeUntil, data.tz).toDate()
      : null,
    duration: data.duration != null ? data.duration : null
  };
};

export const formatSnoozeDate = (
  duration: number,
  unit: moment.unitOfTime.DurationConstructor = 'days'
): string => {
  if (!duration) {
    return;
  }
  let format = 'MM/DD/YYYY';
  if (unit === 'minute' || unit === 'minutes') {
    format = 'h:mmA';
  }
  const date = moment().add(duration, unit).format(format);
  return `(${date})`;
};

// Duration string looks like '15-minutes' or '1-days'
export const getSnoozeDateFromDuration = (duration: string): string => {
  if (!duration || !duration.length) {
    return;
  }
  const parts = duration.split('-');
  const amount = parseInt(parts[0], 10);
  const unit = parts[1] as moment.unitOfTime.DurationConstructor;
  let date = moment().add(amount, unit);
  if (unit === 'day' || unit === 'days') {
    date = date.startOf('day');
  }
  return date.format();
};

export const getSnoozeDurationOptions = (isQuickSnooze = false): Option[] => {
  const options = [
    {
      label: `15 Mins. ${formatSnoozeDate(15, 'minutes')}`,
      value: '15-minutes'
    },
    {
      label: `30 Mins. ${formatSnoozeDate(30, 'minutes')}`,
      value: '30-minutes'
    },
    {
      label: `1 hour ${formatSnoozeDate(60, 'minutes')}`,
      value: '60-minutes'
    },
    {
      label: `2 hours ${formatSnoozeDate(120, 'minutes')}`,
      value: '120-minutes'
    },
    {
      label: `4 hours ${formatSnoozeDate(240, 'minutes')}`,
      value: '240-minutes'
    }
  ];
  if (isQuickSnooze) {
    return options;
  }
  const additionalOptions = [
    {
      label: `1 Day ${formatSnoozeDate(1)}`,
      value: '1-days'
    },
    {
      label: `3 Days ${formatSnoozeDate(3)}`,
      value: '3-days'
    },
    {
      label: `1 Week ${formatSnoozeDate(7)}`,
      value: '7-days'
    }
  ];
  return [...options, ...additionalOptions];
};

// Hooks

interface GetEventsParams extends PaginatedParams {
  baseId?: number;
  type?: EventTypes;
  app?: AppKey;
  types?: EventTypes[];
  scope?: 'user' | 'account';
}
interface GetEventsResponse extends BaseResponse {
  total: number;
  events: IEvent[];
}
interface UseGetEventsResponse {
  baseId: number;
  isLoading: boolean;
  addToEvents: (moreEvents: IEvent[]) => void;
  total: number;
  events: IEvent[];
}

export const transformResponse = (
  response: GetEventsResponse
): Omit<GetEventsResponse, 'status'> => {
  return {
    total: response.total,
    events: response.events
  };
};

export const useGetEvents = (params: GetEventsParams): UseGetEventsResponse => {
  const { terms } = useMetaData();
  const [baseId, setBaseId] = useState(null);
  const url = '/event/data_for_user/';
  const request = useCallback(
    () =>
      PostJSON({
        url,
        params: {
          types: Object.keys(getEventTypes(terms)),
          ...params
        }
      }),
    [params.page, params.limit, params.type, params.app, params.scope]
  );
  const { data, setData, isLoading } = useRequestJSON<
    GetEventsResponse,
    Omit<GetEventsResponse, 'status'>
  >({
    request,
    transformFn: transformResponse
  });

  const addToEvents = (moreEvents: IEvent[]) => {
    const updated = produce(data, (draft) => {
      draft.events = draft.events.concat(...moreEvents);
    });
    setData(updated);
  };

  // Reset baseId when scope changes
  useEffect(() => {
    setBaseId(null);
  }, [params.scope, setBaseId]);

  useEffect(() => {
    if (data && data.events.length) {
      setBaseId(data.events[0].id);
    }
  }, [data]);

  return {
    baseId,
    isLoading,
    addToEvents,
    total: data?.total || 0,
    events: data?.events || []
  };
};

interface UseGetMoreEventsFn {
  (
    addToEvents: (moreEvents: IEvent[]) => void,
    baseId: number,
    scope?: 'user' | 'account'
  ): {
    fetchMore: (hasMore: boolean) => void;
  };
}

/**
 * This hook allows us to add more to the state provided by useGetEvents
 * without overriding it
 *
 * @param addToEvents function
 */
export const useGetMoreEvents: UseGetMoreEventsFn = (
  addToEvents,
  baseId,
  scope
) => {
  const { terms } = useMetaData();
  const [page, setPage] = useState(1);
  const url = '/event/data_for_user/';
  const request = useCallback(
    () =>
      PostJSON({
        url,
        params: {
          page,
          baseId,
          limit: 5,
          types: Object.keys(getEventTypes(terms)),
          scope
        }
      }),
    [page, baseId, scope]
  );
  const { data, run } = useRequestOnEvent<
    GetEventsResponse,
    Omit<GetEventsResponse, 'status'>
  >();

  const fetchMore = (hasMore: boolean) => {
    if (hasMore) {
      run(request);
      setPage(page + 1);
    }
  };

  useEffect(() => {
    if (data && data.events) {
      addToEvents(data.events);
    }
  }, [data]);

  return {
    fetchMore
  };
};

// Patient events

interface GetPatientEventsParams extends PaginatedParams {
  patientId: number;
  type?: EventTypes;
  app?: AppKey;
  apps?: string[];
  types?: EventTypes[];
}

interface GetPatientEventsResponse extends BaseResponse {
  total: number;
  events: IEvent[];
  hasMore: boolean;
}

interface UseGetPatientEventsResponse {
  isLoading: boolean;
  addToEvents: (moreEvents: IEvent[]) => void;
  addNewEvent: (newEvent: IEvent) => void;
  total: number;
  events: IEvent[];
  hasMore: boolean;
}

export const transformPatientResponse = (
  response: GetPatientEventsResponse
): Omit<GetPatientEventsResponse, 'status'> => {
  return {
    total: response.events.length,
    events: response.events,
    hasMore: response.hasMore
  };
};

// TODO: Propagate updates to all instances of <PatientEvents /> loaded in patient index (e.g. Program Event Notes vs. All Event Notes)
export const useGetPatientEvents = (
  params: GetPatientEventsParams,
  isEnabled?: boolean
): UseGetPatientEventsResponse => {
  const { terms } = useMetaData();
  const [currPage, setCurrPage] = useState(-1);
  const [skipCount, setSkipCount] = useState(0);
  const [eventCache, setEventCache] = useState<IEvent[]>([]);

  const url = '/event/data/';
  const request = useCallback(
    () =>
      PostJSON({
        url,
        params: {
          types: Object.keys(getEventTypes(terms)),
          skip: skipCount,
          ...params
        }
      }),
    [
      params.page,
      params.limit,
      params.app,
      JSON.stringify(params.apps || []),
      params.type,
      params.patientId
    ]
  );
  const { data, setData, isLoading } = useRequestJSON<
    GetPatientEventsResponse,
    Omit<GetPatientEventsResponse, 'status'>
  >({
    request,
    transformFn: transformPatientResponse,
    enabled: isEnabled
  });

  const addToEvents = (moreEvents: IEvent[]) => {
    const _events = eventCache.concat(moreEvents);
    const updated = produce(data, (draft) => {
      draft.events = _events;
    });
    setEventCache(_events);
    setData(updated);
  };

  const addNewEvent = (newEvent: IEvent) => {
    const _events = [newEvent].concat(eventCache);
    const updated = produce(data, (draft) => {
      draft.events = _events;
    });
    setSkipCount(skipCount + 1); // Records to skip until component using this provider is unmounted
    setEventCache(_events);
    setData(updated);
  };

  // Add more to events each time useGetPatientEvents is called since patient events only get MORE
  useEffect(() => {
    if (currPage !== params.page && data && data.events) {
      setCurrPage(params.page);
      addToEvents(data.events);
    }
  }, [data]);

  return {
    isLoading,
    addToEvents,
    addNewEvent,
    total: data?.total || 0,
    events: data?.events || [],
    hasMore: data?.hasMore || false
  };
};

// Edit event note

export interface EditEventNoteParams {
  id: number;
  note: string;
}

export interface EditEventNoteResponse extends BaseResponse {
  event: IEvent;
}

export const EditEventNote = ({
  id,
  note
}: EditEventNoteParams): Promise<EditEventNoteResponse | ErrorResponse> => {
  return PostJSON<EditEventNoteResponse>({
    url: '/event/edit/',
    params: { id, note }
  });
};

export const getInvalidationFieldDisplay = (
  app: AppKey,
  key: string
): string => {
  let fieldDisplay = Capitalize(key);
  if (app === 'epxdepressphq') {
    fieldDisplay = `PHQ Question ${key}`;
  } else if (app === 'epxdepressgad') {
    fieldDisplay = `GAD Question ${key}`;
  }
  return fieldDisplay;
};

export const getInvalidationValueDisplay = (value: any): string => {
  return value === null ? 'N/A' : String(value);
};
